import { useState, useEffect } from 'react';
import { Eye, Download, Trash2, Search, FileText, User, Mail, Phone, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { jobApplicationService, operationLogService } from '@/services/storage';
import type { JobApplication } from '@/types';
import { toast } from 'sonner';

const statusMap: Record<string, { label: string; color: string }> = {
  pending: { label: '待审核', color: 'bg-yellow-100 text-yellow-800' },
  reviewed: { label: '已查看', color: 'bg-blue-100 text-blue-800' },
  interview: { label: '面试中', color: 'bg-green-100 text-green-800' },
  rejected: { label: '已拒绝', color: 'bg-red-100 text-red-800' },
  hired: { label: '已录用', color: 'bg-purple-100 text-purple-800' },
};

export default function AdminJobApplications() {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedApp, setSelectedApp] = useState<JobApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    setApplications(await jobApplicationService.getAll());
  };

  const handleView = async (app: JobApplication) => {
    setSelectedApp(app);
    setDetailOpen(true);
    // 标记为已查看
    if (app.status === 'pending') {
      await jobApplicationService.update(app.id, { status: 'reviewed' });
      loadApplications();
    }
  };

  const handleStatusChange = async (id: string, status: JobApplication['status']) => {
    await jobApplicationService.update(id, { status });
    await operationLogService.add({
      user: 'admin',
      action: '更新简历状态',
      module: '简历投递',
      details: `将简历状态更新为: ${statusMap[status].label}`,
    });
    loadApplications();
    toast.success('状态已更新');
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除此简历投递记录吗？')) {
      await jobApplicationService.delete(id);
      loadApplications();
      toast.success('记录已删除');
    }
  };

  const handleDownloadResume = async (app: JobApplication) => {
    const link = document.createElement('a');
    link.href = app.resumeUrl;
    link.download = app.resumeName;
    link.click();
  };

  const filteredApps = applications.filter(app => {
    const matchesSearch = 
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus ? app.status === filterStatus : true;
    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">简历投递管理</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {Object.entries(statusMap).map(([status, { label }]) => (
          <Card key={status}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-2xl font-bold">
                {applications.filter(a => a.status === status).length}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="搜索姓名、职位、邮箱..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-40">
              <select
                className="w-full border rounded-md px-3 py-2"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">全部状态</option>
                {Object.entries(statusMap).map(([status, { label }]) => (
                  <option key={status} value={status}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>候选人</TableHead>
                <TableHead>应聘职位</TableHead>
                <TableHead>联系方式</TableHead>
                <TableHead>投递时间</TableHead>
                <TableHead>状态</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApps.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="font-medium">{app.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Briefcase className="h-4 w-4 mr-2 text-gray-400" />
                      {app.jobTitle}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="flex items-center">
                        <Mail className="h-3 w-3 mr-1 text-gray-400" />
                        {app.email}
                      </div>
                      <div className="flex items-center mt-1">
                        <Phone className="h-3 w-3 mr-1 text-gray-400" />
                        {app.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(app.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusMap[app.status].color}>
                      {statusMap[app.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => handleView(app)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadResume(app)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(app.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredApps.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无简历投递记录
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>简历详情</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">姓名</Label>
                  <p className="font-medium text-lg">{selectedApp.name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">应聘职位</Label>
                  <p className="font-medium">{selectedApp.jobTitle}</p>
                </div>
                <div>
                  <Label className="text-gray-500">邮箱</Label>
                  <p>{selectedApp.email}</p>
                </div>
                <div>
                  <Label className="text-gray-500">电话</Label>
                  <p>{selectedApp.phone}</p>
                </div>
              </div>

              {/* Cover Letter */}
              {selectedApp.coverLetter && (
                <div>
                  <Label className="text-gray-500">自我介绍</Label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm whitespace-pre-wrap">{selectedApp.coverLetter}</p>
                  </div>
                </div>
              )}

              {/* Resume */}
              <div>
                <Label className="text-gray-500">简历文件</Label>
                <div className="mt-2 flex items-center p-4 bg-gray-50 rounded-lg">
                  <FileText className="h-8 w-8 text-blue-500 mr-3" />
                  <div className="flex-1">
                    <p className="font-medium">{selectedApp.resumeName}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadResume(selectedApp)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    下载
                  </Button>
                </div>
              </div>

              {/* Status Actions */}
              <div>
                <Label className="text-gray-500">状态更新</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(statusMap).map(([status, { label }]) => (
                    <Button
                      key={status}
                      variant={selectedApp.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleStatusChange(selectedApp.id, status as JobApplication['status'])}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

import { Label } from '@/components/ui/label';
